import React, { useState, useRef, useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { ChevronDown, ArrowUpDown } from 'lucide-react'
import {
    ApiShopperLandingPageLink,
    ApiProductSortOption,
    getProductSortOptionLabel,
    PRODUCT_SORT_OPTIONS,
} from '@storefronts/common-types'
import { useProductSort } from '../../context/ProductSortContext'
import {
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
} from '../../context/CustomizationContextProvider'
import { getTypographyRoleStyle } from '../../style/typography'
import { scaledFontSize } from '../../style/scrollingLayout'

// Animation duration constants
const ANIMATION_DURATION_MS = 200
const ANIMATION_DURATION_S = '0.2s'

const SortContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
`

const CustomSelectWrapper = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
`

const CustomSelectTrigger = styled.button({
    background: 'transparent',
    border: 'none',
    padding: 0,
    ...getTypographyRoleStyle('navText', {
        fontWeight: 500,
    }),
    fontSize: scaledFontSize(12),
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    transition: 'opacity 0.15s ease',
    opacity: 0.7,
    '&:focus': {
        outline: 'none',
    },
    '&:hover': {
        opacity: 1,
    },
})

const CaretIconWrapper = styled.div<{ isOpen: boolean }>`
    display: flex;
    align-items: center;
    transition: transform ${ANIMATION_DURATION_S} ease;
    transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    flex-shrink: 0;
`

const slideDown = keyframes`
    from {
        opacity: 0;
        transform: translateY(-8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`

const slideUp = keyframes`
    from {
        opacity: 1;
        transform: translateY(0);
    }
    to {
        opacity: 0;
        transform: translateY(-8px);
    }
`

const DropdownMenu = styled.div<{ isOpen: boolean; isClosing: boolean }>`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: var(${PRIMARY_PAGE_FILL_COLOR_VAR});
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 0.2rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    min-width: 144px;
    z-index: 1000;
    animation: ${(props) => (props.isClosing ? slideUp : slideDown)}
        ${ANIMATION_DURATION_S} ease;
    display: ${(props) => (props.isOpen || props.isClosing ? 'block' : 'none')};
`

const DropdownOption = styled.button<{ isSelected: boolean }>(
    ({ isSelected }) => ({
        width: '100%',
        padding: '10px 12px',
        ...getTypographyRoleStyle('navText', {
            fontWeight: 400,
        }),
        fontSize: scaledFontSize(12),
        textAlign: 'left',
        background: isSelected
            ? `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`
            : `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
        color: isSelected
            ? `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`
            : `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
        border: 'none',
        cursor: 'pointer',
        transition: 'opacity 0.15s ease',
        '&:first-of-type': {
            borderRadius: '0.2rem 0.2rem 0 0',
        },
        '&:last-of-type': {
            borderRadius: '0 0 0.2rem 0.2rem',
        },
        '&:only-child': {
            borderRadius: '0.2rem',
        },
        '&:hover': {
            opacity: isSelected ? 1 : 0.7,
        },
    }),
)

const DEFAULT_PRODUCT_COUNT_THRESHOLD = 8

interface ProductSortDropdownProps {
    products?: ApiShopperLandingPageLink[]
}

export const ProductSortDropdown: React.FC<ProductSortDropdownProps> = ({
    products,
}) => {
    const { sortOption, setSortOption } = useProductSort()
    const [isOpen, setIsOpen] = useState(false)
    const [isClosing, setIsClosing] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    const productCount = products?.length || 0

    // Check if any products have ratings
    const hasProductsWithRatings = useMemo(() => {
        if (!products || products.length === 0) {
            return false
        }
        return products.some(
            (product) => product.reviews?.rating && product.reviews?.rating > 0,
        )
    }, [products])

    // Filter out star-rating when this section has no rated products
    const availableSortOptions = useMemo(() => {
        if (hasProductsWithRatings) {
            return PRODUCT_SORT_OPTIONS
        }
        return PRODUCT_SORT_OPTIONS.filter((option) => option !== 'star-rating')
    }, [hasProductsWithRatings])

    const handleSortChange = (option: ApiProductSortOption) => {
        setSortOption(option)
        handleClose()
    }

    const handleToggle = () => {
        if (isOpen) {
            handleClose()
        } else {
            setIsOpen(true)
            setIsClosing(false)
        }
    }

    const handleClose = () => {
        setIsClosing(true)
        setTimeout(() => {
            setIsOpen(false)
            setIsClosing(false)
        }, ANIMATION_DURATION_MS)
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                if (isOpen) {
                    handleClose()
                }
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    // Don't show sorting if product count is below threshold
    if (productCount < DEFAULT_PRODUCT_COUNT_THRESHOLD) {
        return null
    }

    const sortLabelId = 'product-sort-label'

    return (
        <SortContainer>
            {/* Visually hidden label for screen readers */}
            <span
                id={sortLabelId}
                style={{
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    borderWidth: 0,
                }}
            >
                Sort by:
            </span>
            <CustomSelectWrapper ref={dropdownRef}>
                <CustomSelectTrigger
                    onClick={handleToggle}
                    aria-label="Sort products by"
                    aria-labelledby={sortLabelId}
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                >
                    <ArrowUpDown size={14} />
                    {getProductSortOptionLabel(sortOption)}
                    <CaretIconWrapper isOpen={isOpen}>
                        <ChevronDown size={14} />
                    </CaretIconWrapper>
                </CustomSelectTrigger>
                <DropdownMenu
                    isOpen={isOpen}
                    isClosing={isClosing}
                    role="listbox"
                    aria-labelledby={sortLabelId}
                >
                    {availableSortOptions.map((option) => (
                        <DropdownOption
                            key={option}
                            isSelected={option === sortOption}
                            onClick={() => handleSortChange(option)}
                            role="option"
                            aria-selected={option === sortOption}
                        >
                            {getProductSortOptionLabel(option)}
                        </DropdownOption>
                    ))}
                </DropdownMenu>
            </CustomSelectWrapper>
        </SortContainer>
    )
}
